import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { OrderCard } from '../components/OrderCard';
import { OrderListItem } from '../components/OrderListItem';
import { PaymentCard } from '../components/PaymentCard';
import { TotalPaymentRow } from '../components/TotalPaymentRow';
import { BalanceContextType, BalancesItem } from '../context/BalanceContext';
import { withSelectedOrder, withTotalPayment } from '../utils';

type BalanceListProps = {
  balances: BalanceContextType;
  readonly?: boolean;
  setBalanceItemToUsePoint?: (item: BalancesItem | null) => void;
  setBalanceItemChangeAmount?: (item: BalancesItem | null) => void;
  setBalancesSelection?: (balancesSelection?: string[]) => void;
  balancesSelection: string[];
  handlePaymentAmountChange?: (
    it: BalancesItem,
    amount: number,
  ) => Promise<void>;
  handleLoyaltyPointsRemove?: (balance: BalancesItem) => void;
};

export function BalanceList({
  balances,
  readonly,
  setBalanceItemToUsePoint,
  handlePaymentAmountChange,
  setBalancesSelection,
  balancesSelection,
  handleLoyaltyPointsRemove,
}: BalanceListProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const matchesXl = useMediaQuery(theme.breakpoints.up('xl'));
  const selectedBalances = withSelectedOrder(balances, balancesSelection);
  const totalPayment = withTotalPayment(selectedBalances);
  const { t } = useTranslation();

  const handleSelection = async (it: BalancesItem) => {
    setBalancesSelection &&
      balancesSelection &&
      setBalancesSelection(balancesSelection.concat(it.orderNumber));
  };

  const handleDeselection = (it: BalancesItem) => {
    setBalancesSelection &&
      balancesSelection &&
      setBalancesSelection(
        balancesSelection.filter((s) => s !== it.orderNumber),
      );
  };

  return (
    <Box>
      {isMobile ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            mt: 1,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          {balances.map((balance, i) => (
            <>
              {readonly ? (
                <PaymentCard
                  key={balance.orderNumber}
                  firstName={balance.firstName}
                  lastName={balance.lastName}
                  balance={balance.payment.balance}
                  total={balance.payment.total}
                  toPay={balance.payment.toPay}
                  loyalty={balance.payment.loyalty}
                />
              ) : (
                <OrderCard
                  key={balance.orderNumber}
                  order={i + 1}
                  firstName={balance.firstName}
                  lastName={balance.lastName}
                  readonly={balance.payment.total.amount === 0}
                  tourName={balance.tourName}
                  tourStartDate={balance.tourStartDate}
                  bedTypeName={balance.bedTypeName}
                  pnr={balance.pnr}
                  orderNumber={balance.orderNumber}
                  balance={balance.payment.balance}
                  total={balance.payment.total}
                  received={balance.payment.received}
                  toPay={balance.payment.toPay}
                  loyalty={balance.payment.loyalty}
                  selected={
                    balancesSelection &&
                    balancesSelection.includes(balance.orderNumber)
                  }
                  onSelection={(v) =>
                    v ? handleSelection(balance) : handleDeselection(balance)
                  }
                  onPayWithLoyaltyClick={
                    setBalanceItemToUsePoint
                      ? () => setBalanceItemToUsePoint(balance)
                      : undefined
                  }
                  onRemoveLoyaltyPoints={
                    handleLoyaltyPointsRemove
                      ? () => handleLoyaltyPointsRemove(balance)
                      : undefined
                  }
                  onChangeAmount={(amount: number) =>
                    handlePaymentAmountChange &&
                    handlePaymentAmountChange(balance, amount)
                  }
                />
              )}
            </>
          ))}
        </Box>
      ) : (
        <table
          className="table_point"
          style={{
            marginTop: 24,
            marginBottom: 24,
            border: 'none',
          }}
        >
          <thead>
            <tr>
              <th style={{ width: matchesXl ? 'auto' : 170 }}>
                <Typography sx={{ color: 'inherit' }}>
                  {t('balancesPage.customer')}
                </Typography>
              </th>
              <th style={{ width: matchesXl ? 'auto' : 170 }}>
                <Typography sx={{ color: 'inherit' }}>
                  {t('balancesPage.payAmount')}
                </Typography>
              </th>
              <th>
                <Typography sx={{ color: 'inherit' }}>
                  {t('balancesPage.orderPay')}
                </Typography>
              </th>
              <th>
                <Typography sx={{ color: 'inherit' }}>
                  {t('balancesPage.paymentMade')}
                </Typography>
              </th>
              <th>
                <Typography sx={{ color: 'inherit' }}>
                  {t('balancesPage.tour')}
                </Typography>
              </th>
              <th>
                <Typography sx={{ color: 'inherit' }}>
                  {t('balancesPage.tourDate')}
                </Typography>
              </th>
              <th>
                <Typography sx={{ color: 'inherit' }}>
                  {t('balancesPage.bedType')}
                </Typography>
              </th>
              <th>
                <Typography sx={{ color: 'inherit' }}>
                  {t('balancesPage.orderNumber')}
                </Typography>
              </th>
              <th>
                <Typography sx={{ color: 'inherit' }}>
                  {t('balancesPage.pnr')}
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {balances?.map((balance, i) => (
              <OrderListItem
                key={balance.orderNumber}
                readonly={readonly || balance.payment.total.amount === 0}
                order={i + 1}
                firstName={balance.firstName}
                lastName={balance.lastName}
                tourName={balance.tourName}
                tourStartName={balance.tourStartDate}
                pnr={balance.pnr}
                orderNumber={balance.orderNumber}
                bedTypeName={balance.bedTypeName}
                balance={balance.payment.balance}
                total={balance.payment.total}
                received={balance.payment.received}
                toPay={balance.payment.toPay}
                loyalty={balance.payment.loyalty}
                selected={
                  balancesSelection &&
                  balancesSelection.includes(balance.orderNumber)
                }
                onSelection={(v) =>
                  v ? handleSelection(balance) : handleDeselection(balance)
                }
                onPayWithLoyaltyClick={
                  setBalanceItemToUsePoint
                    ? () => setBalanceItemToUsePoint(balance)
                    : undefined
                }
                onRemoveLoyaltyPoints={
                  handleLoyaltyPointsRemove
                    ? () => handleLoyaltyPointsRemove(balance)
                    : undefined
                }
                onChangeAmount={(amount: number) =>
                  handlePaymentAmountChange &&
                  handlePaymentAmountChange(balance, amount)
                }
              />
            ))}
          </tbody>
          {selectedBalances.length > 0 && <TotalPaymentRow {...totalPayment} />}
        </table>
      )}
    </Box>
  );
}
